/** Types shared by the React client and the Netlify Functions API. */

export const ROLES = {
  member: 'Member',
  arb: 'Architecture Review Board',
  board: 'Board Member',
  organizer: 'Event Organizer',
  admin: 'Administrator',
  superadmin: 'Super Admin',
} as const;

export type Role = keyof typeof ROLES;

export const ALL_ROLES = Object.keys(ROLES) as Role[];

/** Roles a community board can grant. Super Admin is platform-only. */
export const COMMUNITY_ROLES = ALL_ROLES.filter((role) => role !== 'superadmin') as Role[];

export type MembershipStatus = 'active' | 'suspended';
export type CommunityStatus = 'active' | 'disabled';
export type InviteStatus =
  | 'pending'
  | 'sent'
  | 'accepted'
  | 'expired'
  | 'revoked'
  | 'delivery_failed';

export const EMAIL_CONSENT_VERSION = 'email-communications-v1';

export interface User {
  id: string;
  email: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  unit: string | null;
  phone: string | null;
  avatarUrl: string | null;
  roles: Role[];
  communityId: string | null;
  /**
   * Set only for a Super Admin who chose a community to manage. `communityId`
   * then points at that community, and the Super Admin acts as its admin.
   */
  actingCommunityId?: string | null;
  isTenant: boolean;
  membershipStatus: MembershipStatus;
  emailConsentAt: string | null;
  createdAt: string;
}

export interface Community {
  id: string;
  name: string;
  address: string;
  managementEmail: string | null;
  managementPhone: string | null;
  boardEmail: string | null;
  boardContactName: string | null;
  boardContactPhone: string | null;
  status: CommunityStatus;
  disabledAt: string | null;
  disabledReason: string | null;
  primaryContactUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CommunityStats {
  community: Community;
  members: number;
  tenants: number;
  boardMembers: number;
  activeMembers: number;
  invitedMembers: number;
  openIssues: number;
  openViolations: number;
  pendingArchitecture: number;
  upcomingEvents: number;
}

export interface MemberInvite {
  code: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  unit: string | null;
  roles: Role[];
  note: string | null;
  status: InviteStatus;
  isTenant: boolean;
  maxUses: number;
  usedCount: number;
  expiresAt: string | null;
  lastSentAt: string | null;
  createdAt: string;
}

export interface CommunityPublicInfo {
  name: string;
  address: string;
  managementEmail: string | null;
  managementPhone: string | null;
  boardEmail: string | null;
  boardContactName: string | null;
  boardContactPhone: string | null;
  boardMemberNames: string[];
}

export interface BulkImportJob {
  id: string;
  communityId: string;
  filename: string;
  status: 'preview' | 'committed' | 'failed';
  totalRows: number;
  validRows: number;
  invalidRows: number;
  importedRows: number;
  invitedRows: number;
  errorSummary: string | null;
  createdAt: string;
  committedAt: string | null;
}

export interface BulkImportRow {
  id: string;
  rowNumber: number;
  payload: Record<string, string>;
  valid: boolean;
  errors: string[];
  imported: boolean;
}

export interface Attachment {
  id: string;
  url: string;
  filename: string;
  contentType: string;
  size: number;
  uploadedBy?: string;
  createdAt?: string;
}

export interface Note {
  id: string;
  authorId: string;
  authorName: string;
  authorRoles: Role[];
  body: string;
  internal: boolean;
  createdAt: string;
  attachments: Attachment[];
  /** Sources a reviewer attached to the note (architecture only). */
  citations?: KnowledgeCitation[];
}

/* ---------------------------------------------------------------- architecture */

export const ARCH_STATUSES = [
  'Draft',
  'Submitted',
  'In Review',
  'Info Requested',
  'Approved',
  'Denied',
  'Withdrawn',
] as const;
export type ArchStatus = (typeof ARCH_STATUSES)[number];

export const PROJECT_TYPES = [
  'Fence',
  'Exterior Paint',
  'Roofing',
  'Solar',
  'Shed / Outbuilding',
  'Deck / Patio',
  'Driveway',
  'Landscaping',
  'Addition',
  'Windows / Doors',
  'Pool / Spa',
  'Other',
] as const;
export type ProjectType = (typeof PROJECT_TYPES)[number];

export type AiVerdict = 'likely_compliant' | 'needs_attention' | 'likely_non_compliant' | 'unknown';

export interface AiFinding {
  guidelineId: string | null;
  guidelineTitle: string;
  category: string;
  severity: 'info' | 'warning' | 'blocker';
  assessment: string;
  requirement: string;
}

export interface AiReview {
  id: string;
  submissionId: string;
  verdict: AiVerdict;
  summary: string;
  findings: AiFinding[];
  missingInfo: string[];
  engine: 'rules' | 'gemini';
  model: string | null;
  createdAt: string;
}

export interface ArchVote {
  userId: string;
  name: string;
  vote: 'approve' | 'deny';
  comment: string | null;
  createdAt: string;
}

export interface ArchSubmission {
  id: string;
  reference: string;
  authorId: string;
  authorName: string;
  authorUnit: string | null;
  title: string;
  description: string;
  projectType: ProjectType;
  contractor: string | null;
  estimatedStart: string | null;
  estimatedCost: number | null;
  status: ArchStatus;
  decisionReason: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
  updatedAt: string;
  attachments: Attachment[];
  notes: Note[];
  aiReview: AiReview | null;
  votes: ArchVote[];
  arbMemberCount: number;
  assignedReviewerIds: string[];
  checklist: ChecklistItem[];
  sourceRefs: ArchSourceRef[];
  decisionCitations: KnowledgeCitation[];
  /** Reviewers only; owners never receive committee research. */
  research: ArchResearchNote[];
}

export interface Guideline {
  id: string;
  category: string;
  title: string;
  body: string;
  keywords: string[];
  projectTypes: ProjectType[];
  active: boolean;
  updatedAt: string;
}

/* ------------------------------------------------------------------ complaints */

export const ISSUE_KINDS = ['Complaint', 'Assistance request', 'Maintenance', 'Suggestion'] as const;
export type IssueKind = (typeof ISSUE_KINDS)[number];

export const ISSUE_STATUSES = ['Open', 'Acknowledged', 'In Progress', 'Resolved', 'Closed'] as const;
export type IssueStatus = (typeof ISSUE_STATUSES)[number];

export const PRIORITIES = ['Low', 'Normal', 'High', 'Urgent'] as const;
export type Priority = (typeof PRIORITIES)[number];

export interface Issue {
  id: string;
  reference: string;
  authorId: string;
  authorName: string;
  authorUnit: string | null;
  anonymous: boolean;
  kind: IssueKind;
  title: string;
  description: string;
  location: string | null;
  status: IssueStatus;
  priority: Priority;
  assignedTo: string | null;
  assignedToName: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  attachments: Attachment[];
  notes: Note[];
}

/* ---------------------------------------------------------------------- events */

export interface EventRsvp {
  userId: string;
  userName: string;
  status: 'going' | 'maybe' | 'not_going';
  guests: number;
}

export interface CommunityEvent {
  id: string;
  title: string;
  description: string;
  location: string | null;
  startsAt: string;
  endsAt: string | null;
  allDay: boolean;
  rsvpEnabled: boolean;
  rsvpLimit: number | null;
  commentsEnabled: boolean;
  published: boolean;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  attachments: Attachment[];
  rsvps: EventRsvp[];
  notes: Note[];
  myRsvp: EventRsvp['status'] | null;
  goingCount: number;
}

/* ------------------------------------------------------------------ board hub */

export interface ActionItem {
  id: string;
  title: string;
  detail: string | null;
  done: boolean;
  dueDate: string | null;
  assignedTo: string | null;
  assignedToName: string | null;
  linkedType: 'issue' | 'arch' | null;
  linkedId: string | null;
  createdBy: string;
  createdAt: string;
  completedAt: string | null;
}

export interface ActivityEntry {
  id: string;
  actorName: string | null;
  verb: string;
  entityType: string;
  entityId: string | null;
  summary: string;
  createdAt: string;
}

export interface BoardHubSummary {
  issues: { open: number; inProgress: number; resolved: number; urgent: number };
  architecture: { pending: number; approved: number; denied: number };
  events: { upcoming: number };
  violations: { open: number; noticeSent: number; resolved: number };
  finance: { delinquentTotal: number; ytdExpenses: number; annualBudget: number; projectedYearEnd: number } | null;
  recentIssues: Pick<Issue, 'id' | 'reference' | 'title' | 'status' | 'priority' | 'createdAt'>[];
  recentSubmissions: Pick<ArchSubmission, 'id' | 'reference' | 'title' | 'status' | 'createdAt'>[];
  recentViolations: Pick<Violation, 'id' | 'reference' | 'propertyAddress' | 'status' | 'createdAt'>[];
  activity: ActivityEntry[];
}

/* ------------------------------------------------------------------ financials */

export interface DelinquentAccount {
  id: string;
  unit: string;
  ownerName: string | null;
  amount: number;
  daysPastDue: number;
  inCollections: boolean;
}

export interface VendorExpense {
  id: string;
  vendor: string;
  category: string | null;
  amount: number;
}

export interface MonthlyActual {
  month: number;
  income: number;
  expenses: number;
}

export interface FinancialSnapshot {
  id: string;
  year: number;
  throughMonth: number;
  label: string;
  sourceFilename: string | null;
  sourceUrl: string | null;
  annualBudget: number;
  reserveBalance: number | null;
  operatingBalance: number | null;
  notes: string | null;
  uploadedBy: string;
  uploadedByName: string;
  createdAt: string;
  delinquents: DelinquentAccount[];
  vendors: VendorExpense[];
  monthly: MonthlyActual[];
}

export interface FinancialProjection {
  cumulativeActual: (number | null)[];
  cumulativeProjected: (number | null)[];
  monthlyBurnRate: number;
  projectedYearEnd: number;
  annualBudget: number;
  variance: number;
  overBudget: boolean;
  delinquentTotal: number;
  topVendors: VendorExpense[];
}

/* ---------------------------------------------------------------- violations */

export const VIOLATION_STATUSES = [
  'Open',
  'Acknowledged',
  'Notice sent',
  'Resolved',
  'Dismissed',
] as const;
export type ViolationStatus = (typeof VIOLATION_STATUSES)[number];

export interface ViolationTag {
  id: string;
  name: string;
  slug: string;
  system: boolean;
}

export interface Violation {
  id: string;
  reference: string;
  authorId: string;
  authorName: string;
  authorUnit: string | null;
  propertyAddress: string;
  description: string;
  status: ViolationStatus;
  tags: ViolationTag[];
  createdAt: string;
  updatedAt: string;
  attachments: Attachment[];
  notes: Note[];
}

/* -------------------------------------------------------------------- bylaws */

export type BylawDocumentStatus = 'processing' | 'ready' | 'failed';

export interface BylawDocument {
  id: string;
  title: string;
  filename: string;
  contentType: string;
  size: number;
  status: BylawDocumentStatus;
  error: string | null;
  uploadedByName: string | null;
  createdAt: string;
  updatedAt: string;
  url: string;
  /** Architecture documents only. */
  visibility?: 'owners' | 'committee';
  category?: string;
  published?: boolean;
}

export interface BylawCitation {
  documentTitle: string;
  excerpt: string;
  /** Where the quote sits, e.g. "Page 4 · Article IV, Section 2". Older answers have none. */
  location?: string;
}

export interface BylawMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations: BylawCitation[];
  /** Architecture chat only: the answer split into HOA, city/county and open questions. */
  answer?: KnowledgeAnswerView | null;
  createdAt: string;
}

export interface BylawChat {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

/* ------------------------------------------------------------------ newsletter */

export interface Newsletter {
  id: string;
  year: number;
  month: number;
  title: string;
  body: string;
  status: 'draft' | 'published';
  engine: 'template' | 'gemini' | null;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
}

/* ----------------------------------------------------------------------- api */

export interface ApiError {
  error: string;
  details?: unknown;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/* ------------------------------------------- architecture knowledge center */

export type KnowledgeSourceFilter = 'all' | 'hoa' | 'city';
export type KnowledgeSection = 'hoa' | 'municipal';
export type KnowledgeVisibility = 'owners' | 'committee';

export const KNOWLEDGE_CATEGORIES = {
  adu: 'ADUs',
  zoning: 'Zoning',
  permits: 'Permits',
  building_code: 'Building code',
  architecture: 'Architecture & design',
  fences: 'Fences',
  decks: 'Decks',
  pools: 'Pools',
  landscaping: 'Landscaping',
  parking: 'Parking',
  streets: 'Streets & roads',
  setbacks: 'Setbacks',
  str: 'Short-term rentals',
  property_lookup: 'Property lookup',
  drainage: 'Drainage',
  utilities: 'Utilities',
  general: 'General',
} as const;
export type KnowledgeCategory = keyof typeof KNOWLEDGE_CATEGORIES;

export const KNOWLEDGE_AUTHORITIES = {
  city: 'City',
  county: 'County',
  state: 'State',
  utility: 'Utility',
  transportation: 'Transportation agency',
  hoa: 'HOA',
  other: 'Other',
} as const;
export type KnowledgeAuthority = keyof typeof KNOWLEDGE_AUTHORITIES;

export interface KnowledgeCitation {
  sourceKey: string;
  kind: 'document' | 'source';
  sourceId: string;
  versionId: string | null;
  title: string;
  authorityName: string;
  section: KnowledgeSection;
  official: boolean;
  excerpt: string;
  location: string | null;
  url: string;
  checkedAt: string | null;
  stale: boolean;
}

export interface KnowledgeResourceLink {
  kind: 'document' | 'source';
  id: string;
  title: string;
  authorityName: string;
  category: string;
  url: string;
  linkOnly: boolean;
  official: boolean;
  checkedAt: string | null;
}

export interface KnowledgeAnswerView {
  hoa: { found: boolean; summary: string; citations: KnowledgeCitation[] };
  municipal: { found: boolean; summary: string; citations: KnowledgeCitation[] };
  unresolved: string[];
  followUps: string[];
  missingMaterials: string[];
  conflicts: string[];
  suggestedProjectType: ProjectType | null;
  links: KnowledgeResourceLink[];
}

export type KnowledgeSourceStatus = 'sync_pending' | 'indexed' | 'link_only' | 'failed' | 'needs_review' | 'archived';

export interface KnowledgeSource {
  id: string;
  title: string;
  authority: KnowledgeAuthority;
  authorityName: string;
  category: KnowledgeCategory;
  description: string;
  url: string;
  jurisdiction: string;
  tags: string[];
  visibility: KnowledgeVisibility;
  priority: number;
  official: boolean;
  ingestionMode: 'ingest' | 'link_only' | 'dataset';
  crawlDepth: number;
  maxPages: number;
  syncCadence: 'manual' | 'daily' | 'weekly' | 'monthly';
  status: KnowledgeSourceStatus;
  stale: boolean;
  linkOnlyReason: string | null;
  lastError: string | null;
  lastCheckedAt: string | null;
  lastIndexedAt: string | null;
  effectiveDate: string | null;
  published: boolean;
  publishedVersion: number | null;
  approvedByName: string | null;
  approvedAt: string | null;
  pendingVersion: {
    id: string;
    version: number;
    fetchedAt: string;
    change: { substantive: boolean; ratio: number; added?: number; removed?: number } | null;
    flags: string[];
  } | null;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface KnowledgeSourceVersion {
  id: string;
  version: number;
  status: 'pending_review' | 'published' | 'superseded' | 'rejected';
  finalUrl: string;
  pageTitle: string | null;
  fetchedAt: string;
  effectiveDate: string | null;
  change: { substantive: boolean; ratio: number; added?: number; removed?: number } | null;
  flags: string[];
  pages: { url: string; title: string }[];
  reviewedByName: string | null;
  reviewedAt: string | null;
  chars: number;
}

export interface KnowledgeSourceDetail {
  source: KnowledgeSource;
  versions: KnowledgeSourceVersion[];
  jobs: { id: string; status: string; attempt: number; error: string | null; outcome: string | null; createdAt: string; finishedAt: string | null }[];
  history: { action: string; actorName: string | null; createdAt: string; metadata: Record<string, unknown> }[];
}

export interface CommunityJurisdiction {
  municipality: string | null;
  county: string | null;
  stateRegion: string | null;
  country: string;
  timezone: string | null;
  boundaryRef: string | null;
  notes: string | null;
  verifiedByName: string | null;
  verifiedAt: string | null;
  mailingAddress: string;
}

export interface KnowledgeResources {
  jurisdiction: CommunityJurisdiction;
  documents: { id: string; title: string; category: string; url: string; visibility: KnowledgeVisibility; updatedAt: string }[];
  sources: Pick<
    KnowledgeSource,
    'id' | 'title' | 'authority' | 'authorityName' | 'category' | 'description' | 'url' | 'visibility' | 'official' | 'ingestionMode' | 'status' | 'stale' | 'linkOnlyReason' | 'lastCheckedAt' | 'effectiveDate'
  >[];
}

export interface ChecklistItem {
  id: string;
  label: string;
  required: boolean;
  done?: boolean;
}

export interface ArchSourceRef {
  kind: 'document' | 'source';
  sourceId: string;
  title: string;
  url: string | null;
  excerpt: string | null;
}

export interface ArchResearchNote {
  id: string;
  authorName: string | null;
  body: string;
  sections: {
    hoa?: string;
    municipal?: string;
    unresolved?: string[];
    missingMaterials?: string[];
    conflicts?: string[];
  };
  citations: KnowledgeCitation[];
  aiGenerated: boolean;
  model: string | null;
  createdAt: string;
  updatedAt: string;
}
